import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, X, Check, ToggleLeft, ToggleRight } from 'lucide-react';
import { adminOccasionsApi, Occasion } from '../services/api';
import { toast } from 'sonner';

const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  { label: 'Celebration', emojis: ['🎉','🎊','🥳','🎈','🎁','🎂','🍰','🥂','🍾','🎆','🎇','✨','⭐','🌟','💫','🎗'] },
  { label: 'Love & Hearts', emojis: ['❤️','💕','💝','💖','💗','💓','💞','💌','💘','🩷','🫶','😍','🥰','😘','💑','💏'] },
  { label: 'People & Faces', emojis: ['😊','😄','😁','🥹','🤗','😎','🙏','👏','🤝','🫂','👍','🌹','💐','🌷','🌸','🌺'] },
  { label: 'Occasions', emojis: ['🎓','💼','💍','🏆','🎖','🥇','🎯','🚀','🌍','⚡','🔥','💡','🌈','🍀','🌙','☀️'] },
  { label: 'Symbols', emojis: ['✅','🕊','🌿','🧘','💪','🏅','🎵','🎶','📚','🖊','💬','📣','🔔','🌻','🍁','🦋'] },
];

export default function Occasions() {
  const [occasions, setOccasions] = useState<Occasion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOccasion, setEditingOccasion] = useState<Occasion | null>(null);
  const [form, setForm] = useState({ value: '', label: '', emoji: '✨', order: 0 });
  const [isSaving, setIsSaving] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchOccasions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const fetchOccasions = async () => {
    try {
      setIsLoading(true);
      const data = await adminOccasionsApi.getAll();
      setOccasions(data.occasions);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load occasions');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setEditingOccasion(null);
    setForm({ value: '', label: '', emoji: '✨', order: occasions.length });
    setShowForm(true);
  };

  const openEdit = (occ: Occasion) => {
    setEditingOccasion(occ);
    setForm({ value: occ.value, label: occ.label, emoji: occ.emoji, order: occ.order });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingOccasion(null);
    setShowEmojiPicker(false);
  };

  const handleSave = async () => {
    if (!form.label.trim() || (!editingOccasion && !form.value.trim())) {
      toast.error('Value and label are required');
      return;
    }
    setIsSaving(true);
    try {
      if (editingOccasion) {
        await adminOccasionsApi.update(editingOccasion._id, {
          label: form.label,
          emoji: form.emoji,
          order: form.order,
        });
        toast.success('Occasion updated');
      } else {
        await adminOccasionsApi.create({
          value: form.value,
          label: form.label,
          emoji: form.emoji,
          order: form.order,
        });
        toast.success('Occasion created');
      }
      closeForm();
      fetchOccasions();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save occasion');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (occ: Occasion) => {
    try {
      await adminOccasionsApi.update(occ._id, { isActive: !occ.isActive });
      setOccasions(prev =>
        prev.map(o => (o._id === occ._id ? { ...o, isActive: !occ.isActive } : o))
      );
      toast.success(`Occasion ${!occ.isActive ? 'enabled' : 'disabled'}`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update occasion');
    }
  };

  const handleDelete = async (occ: Occasion) => {
    if (!confirm(`Delete occasion "${occ.label}"? This cannot be undone.`)) return;
    try {
      await adminOccasionsApi.delete(occ._id);
      setOccasions(prev => prev.filter(o => o._id !== occ._id));
      toast.success('Occasion deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete occasion');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Occasions</h2>
          <p className="text-slate-500 text-sm mt-1">
            Manage occasions shown in the "Personalize & Send" feature
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
        >
          <Plus size={16} />
          Add Occasion
        </button>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Order</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Emoji</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Label</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Value</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Status</th>
                <th className="text-left px-6 py-3 text-slate-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {occasions.map(occ => (
                <tr key={occ._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-slate-500">{occ.order}</td>
                  <td className="px-6 py-4 text-2xl">{occ.emoji}</td>
                  <td className="px-6 py-4 font-medium text-slate-800">{occ.label}</td>
                  <td className="px-6 py-4 text-slate-500 font-mono text-xs">{occ.value}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(occ)}
                      className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full transition-colors ${
                        occ.isActive
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      }`}
                    >
                      {occ.isActive ? (
                        <><ToggleRight size={14} /> Active</>
                      ) : (
                        <><ToggleLeft size={14} /> Inactive</>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(occ)}
                        className="p-1.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                      >
                        <Edit size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(occ)}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {occasions.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-slate-400">
                    No occasions yet. Click "Add Occasion" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingOccasion ? 'Edit Occasion' : 'New Occasion'}
              </h3>
              <button onClick={closeForm} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {!editingOccasion && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Value <span className="text-slate-400 text-xs">(unique key, lowercase, no spaces)</span>
                  </label>
                  <input
                    type="text"
                    value={form.value}
                    onChange={e => setForm(f => ({ ...f, value: e.target.value.toLowerCase().replace(/\s/g, '') }))}
                    placeholder="e.g. birthday"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Label</label>
                <input
                  type="text"
                  value={form.label}
                  onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Birthday"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Emoji</label>
                <div className="relative" ref={emojiPickerRef}>
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(p => !p)}
                    className="flex items-center gap-3 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors bg-white"
                  >
                    <span className="text-2xl leading-none">{form.emoji}</span>
                    <span className="text-slate-500">Click to choose emoji</span>
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-80 max-h-72 overflow-y-auto p-3">
                      {EMOJI_GROUPS.map(group => (
                        <div key={group.label} className="mb-3">
                          <p className="text-xs font-semibold text-slate-400 mb-1.5">{group.label}</p>
                          <div className="grid grid-cols-8 gap-0.5">
                            {group.emojis.map(emoji => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => { setForm(f => ({ ...f, emoji })); setShowEmojiPicker(false); }}
                                className="text-xl h-9 w-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.order}
                  onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-slate-100">
              <button
                onClick={closeForm}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 flex items-center justify-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium transition-colors disabled:opacity-60"
              >
                {isSaving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <><Check size={15} /> Save</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
